/**
 * Reads (and invalidates) the parent-domain cookie Design Tool's cart writes
 * right before checkout navigation (see design-tool's cartSizeQuantityCookie.ts).
 *
 * Why this exists: `v3`'s `/cp/cart-item` endpoint does not persist DT's per-size
 * quantity breakdown — its `apparelSizes` response field is sourced from the
 * apparel catalog's available sizes, not the customer's chosen quantities per
 * size. This cookie carries that breakdown across the DT -> HQ subdomain
 * boundary without a backend change.
 *
 * Once the user edits sizes directly in HQ (Cart drawer), that edit is the
 * freshest truth for that cart item — invalidateCartItemApparelSizesCookie
 * drops the item's entry so a later checkout visit stops preferring DT's
 * now-stale snapshot over the user's own HQ edit.
 */

/** Written by Design Tool cart so HQ can read live per-size quantities. */
const CART_SIZE_QUANTITY_COOKIE_NAME = 'fp_cartSizeQuantities';

/** itemId (stringified) → `code:size:qty` apparelSizes string for that cart line. */
type CartSizeQuantityCookieStore = Record<string, string>;

/**
 * Derives the registrable root domain (e.g. `freshprints.com`, `internal-fp.com`)
 * from the current page's hostname, for use as a cookie's `Domain` attribute so
 * it's readable across DT/HQ subdomains.
 *
 * @returns The last two dot-separated labels of `window.location.hostname`, or
 * `null` when running outside a browser, on `localhost`/`127.0.0.1` (no shared
 * root domain — e.g. local dev), or when the hostname has fewer than two labels.
 */
const getRootDomain = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1') {
    return null;
  }

  const parts = host.split('.');

  if (parts.length < 2) {
    return null;
  }

  return parts.slice(-2).join('.');
};

/**
 * Reads and parses the `fp_cartSizeQuantities` cookie into a store keyed by
 * cart item id.
 *
 * @returns A map of stringified cart item id → `code:size:qty` apparelSizes
 * string. Returns `{}` when running outside a browser, when the cookie is
 * absent, or when its value is missing/malformed/not an object (parse
 * failures are swallowed rather than thrown) — entries whose value isn't a
 * non-empty string are also dropped.
 */
const readStoreFromCookie = (): CartSizeQuantityCookieStore => {
  if (typeof document === 'undefined') {
    return {};
  }

  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${CART_SIZE_QUANTITY_COOKIE_NAME}=`));

    if (!match) {
      return {};
    }

    const value = decodeURIComponent(match.slice(CART_SIZE_QUANTITY_COOKIE_NAME.length + 1));
    const parsed: unknown = JSON.parse(value);

    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    const result: CartSizeQuantityCookieStore = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([itemId, apparelSizes]) => {
      if (typeof apparelSizes === 'string' && apparelSizes) {
        result[itemId] = apparelSizes;
      }
    });

    return result;
  } catch {
    return {};
  }
};

/**
 * Returns the DT-sourced `code:size:qty` apparelSizes string for a cart item id,
 * or `undefined` if the cookie is absent/doesn't cover that item (e.g. local dev,
 * where DT and HQ don't share a root domain, or the handoff cookie expired).
 */
export const getCartItemApparelSizesFromCookie = (itemId: number): string | undefined => {
  if (!Number.isFinite(itemId)) {
    return undefined;
  }

  return readStoreFromCookie()[String(itemId)];
};

/**
 * Drops a single cart item's entry from the DT handoff cookie. Call this when
 * the user edits that item's sizes directly in HQ, so checkout stops
 * preferring DT's now-stale snapshot over the user's own, newer HQ edit.
 * Best-effort: never throws, never blocks the caller.
 */
export const invalidateCartItemApparelSizesCookie = (itemId: number): void => {
  if (typeof document === 'undefined' || !Number.isFinite(itemId)) {
    return;
  }

  try {
    const store = readStoreFromCookie();
    const itemKey = String(itemId);

    if (!(itemKey in store)) {
      return;
    }

    delete store[itemKey];

    const root = getRootDomain();
    if (!root) {
      return;
    }

    if (!Object.keys(store).length) {
      document.cookie = `${CART_SIZE_QUANTITY_COOKIE_NAME}=; Domain=.${root}; Path=/; Max-Age=0; SameSite=Lax; Secure`;
      return;
    }

    const encoded = encodeURIComponent(JSON.stringify(store));
    document.cookie = `${CART_SIZE_QUANTITY_COOKIE_NAME}=${encoded}; Domain=.${root}; Path=/; Max-Age=1800; SameSite=Lax; Secure`;
  } catch {
    // ignore cookie write failures — worst case the stale entry remains until it expires
  }
};
